import * as React from "react";

// Local uploads that get a build-time <file>.webp sibling (see gatsby-node
// generateWebp). Anchored to the start so it only matches ROOT-RELATIVE paths
// (e.g. "/uploads/x.png" from getStaticPath) served from leaderfy.de — NOT
// absolute Strapi URLs (https://…/uploads/x.png), whose .webp does not exist
// and would otherwise 404 the <source> and break the image (picture has no
// fallback from a failed source to the <img>).
const RASTER_UPLOAD = /^\/uploads\/[^?#]+\.(png|jpe?g)$/i;

/**
 * Drop-in replacement for <img> that:
 *  - defaults to lazy loading + async decoding (good for below-the-fold images);
 *  - serves a WebP version via <picture> for /uploads raster images, falling
 *    back to the original (so a missing/failed .webp is harmless).
 *
 * All other props are forwarded unchanged (className / alt / style / key ...).
 * For above-the-fold / LCP images (hero), pass loading="eager".
 * Pass width/height when known to reserve space and avoid layout shift (CLS).
 */
const SmartImg = ({ loading = "lazy", decoding = "async", src, width, height, style, ...props }) => {
  // When intrinsic dimensions are supplied, keep the aspect ratio responsive
  // (height:auto) so the width/height attributes reserve space without forcing
  // a fixed height. Only pass width/height for responsive images — never for
  // object-cover / fixed-size ones.
  const hasDims = width != null && height != null;
  const finalStyle = hasDims ? { height: "auto", ...style } : style;

  const img = (
    <img
      loading={loading}
      decoding={decoding}
      src={src}
      width={width}
      height={height}
      style={finalStyle}
      {...props}
    />
  );

  if (!src || !RASTER_UPLOAD.test(src)) {
    return img;
  }

  // display:contents keeps the <img> in the parent's layout flow, so wrapping
  // in <picture> does not shift existing flex/grid layouts.
  return (
    <picture style={{ display: "contents" }}>
      <source type="image/webp" srcSet={`${src}.webp`} />
      {img}
    </picture>
  );
};

export default SmartImg;
